import { useCallback, useEffect, useRef, useState } from 'react';
import { getFleetCoverage } from '@/services/deals.service.js';

// The deal's CANONICAL per-term aircraft coverage, read from Supabase
// policy_aircraft (AIRCRAFT-CANONICALIZATION-PHASE1-SPEC 1c).
//
// Deliberately a SEPARATE fetch from the account fleet list, which is derived
// from the CRM pipeline cache. Those two stores drift — that drift is the
// disease this phase treats — so the panel that lets the broker fix coverage
// must read the store the carrier request actually reads, not the mirror.
//
// Returns byTail as a Map keyed on the uppercased tail so the fleet list can
// merge canonical coverage onto its CRM-derived rows.
export default function useFleetCoverage(dealId) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(!!dealId);
  const aborted = useRef(false);

  const refresh = useCallback(async () => {
    if (!dealId) return;
    setLoading(true);
    setError(null);
    try {
      const d = await getFleetCoverage(dealId);
      if (!aborted.current) setData(d);
    } catch (err) {
      if (!aborted.current) setError(err);
    } finally {
      if (!aborted.current) setLoading(false);
    }
  }, [dealId]);

  useEffect(() => {
    aborted.current = false;
    if (dealId) refresh();
    return () => {
      aborted.current = true;
    };
  }, [dealId, refresh]);

  const byTail = new Map(
    (data?.aircraft || []).map((a) => [String(a.tail_number || '').toUpperCase(), a])
  );

  return {
    coverage: data,
    byTail,
    bases: data?.bases || [],
    editable: !!data?.editable,
    error,
    loading,
    refresh,
  };
}
